import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { UtilService } from '../../services/util.service';

/**
 * Mensaje del caso mas confuso del producto: la cuenta existe y autentica bien
 * en Firebase (es un usuario valido de la app movil, perfil 'user'), pero el
 * API de la consola solo acepta perfil 'sudo' y responde 401.
 */
const NO_CONSOLE_ACCESS =
  'Tu cuenta no tiene acceso a la consola administrativa. Solo los perfiles sudo pueden entrar.';

/** Traduccion de los codigos de error de Firebase Auth al espanol. */
const FIREBASE_ERRORS: Record<string, string> = {
  'auth/invalid-credential': 'Correo o contraseña incorrectos.',
  'auth/invalid-login-credentials': 'Correo o contraseña incorrectos.',
  'auth/wrong-password': 'Correo o contraseña incorrectos.',
  'auth/user-not-found': 'No existe una cuenta con ese correo electrónico.',
  'auth/invalid-email': 'El correo electrónico no tiene un formato válido.',
  'auth/user-disabled': 'Esta cuenta está deshabilitada. Contacta al administrador.',
  'auth/too-many-requests':
    'Demasiados intentos fallidos. Espera unos minutos antes de volver a intentarlo.',
  'auth/network-request-failed':
    'No hay conexión con el servidor de autenticación. Revisa tu red e inténtalo de nuevo.',
  'auth/operation-not-allowed':
    'El acceso con correo y contraseña está deshabilitado en Firebase.',
  'auth/invalid-api-key': 'La configuración de Firebase de esta consola no es válida.',
  'auth/api-key-not-valid': 'La configuración de Firebase de esta consola no es válida.',
};

/** Pantalla de inicio de sesion de la consola. */
@Component({
  selector: 'app-log-in-page',
  imports: [ReactiveFormsModule],
  templateUrl: './log-in-page.component.html',
  styleUrl: './log-in-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogInPageComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly utilService = inject(UtilService);
  private readonly router = inject(Router);

  protected readonly submitting = signal(false);
  protected readonly showPassword = signal(false);

  /** Error del formulario, visible bajo el boton. Nunca un `alert()`. */
  protected readonly formError = signal('');

  /**
   * Sin la app Web de Firebase configurada el login revienta con un error
   * cripitico; mejor decirlo antes de que nadie escriba su contraseña.
   */
  protected readonly firebaseMissing =
    environment.firebase.apiKey.startsWith('TODO_') ||
    environment.firebase.appId.startsWith('TODO_');

  protected readonly form = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  protected get email() {
    return this.form.controls.email;
  }

  protected get password() {
    return this.form.controls.password;
  }

  protected togglePassword(): void {
    this.showPassword.update((value) => !value);
  }

  protected async submit(): Promise<void> {
    if (this.submitting() || this.firebaseMissing) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formError.set('Revisa los datos del formulario.');
      return;
    }

    const { email, password } = this.form.getRawValue();
    this.formError.set('');
    this.submitting.set(true);

    try {
      // 1. Autenticacion en Firebase.
      try {
        await this.authService.signIn(email.trim(), password);
      } catch (error) {
        this.fail(this.firebaseMessage(error));
        return;
      }

      // 2. El API decide si la cuenta puede entrar a la consola (perfil sudo).
      try {
        const user = await this.authService.getUser();
        if (!user.isSudo) {
          await this.authService.signOut(false);
          this.fail(NO_CONSOLE_ACCESS);
          return;
        }
      } catch (error) {
        await this.authService.signOut(false);
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        this.fail(
          status === 401 || status === 403
            ? NO_CONSOLE_ACCESS
            : this.utilService.errorMessage(
                error,
                'No fue posible validar tu cuenta con el servidor. Inténtalo de nuevo.',
              ),
        );
        return;
      }

      // 3. `replaceUrl` es obligatorio: "atrás" no debe regresar al login.
      await this.router.navigate(['/dashboard'], { replaceUrl: true });
    } finally {
      this.submitting.set(false);
    }
  }

  private fail(message: string): void {
    this.formError.set(message);
    this.toastService.error(message, 'No se pudo iniciar sesión');
    this.password.reset('');
  }

  /** Codigo de Firebase → mensaje en espanol. */
  private firebaseMessage(error: unknown): string {
    const code = (error as { code?: string })?.code ?? '';
    return (
      FIREBASE_ERRORS[code] ??
      this.utilService.errorMessage(error, 'No fue posible iniciar sesión. Inténtalo de nuevo.')
    );
  }
}
